import React, { useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from "@mui/material";

import { ClientSettingsPanel } from "./ClientSettings/ClientSettingsPanel";
import { CalendarSettingsPanel } from "./CalendarSettings/CalendarSettingsPanel";
import { BillSettingsPanel } from "./BillSettings/BillSettingsPanel";
import { HmisSettingsPanel } from "./HmisSettings/HmisSettingsPanel";
import { IpdSettingsPanel } from "./IpdSettings/IpdSettingsPanel";
import MedicalSettingsPanel from "./MedicalSettings/MedicalSettingsPanel";
import { OpdSettingsPanel } from "./OpdSettings/OpdSettingsPanel";
import { StockSettingsPanel } from "./StockSettings/StockSettingsPanel";
import { LabSettingsPanel } from "./LabSettings/LabSettingsPanel";
import { AttendanceSettingsPanel } from "./AttendanceSettings/AttendanceSettingsPanel";
import useModuleSubscriptions from "../../../hooks/useModuleSubscriptions";
import { PayrollSettingsPanel } from "./PayrollSettings/PayrollSettingsPanel";

function getSettings({
  isAttendanceSubscribed = false,
  isStockSubscribed = false,
  isBillSubscribed = false,
  isClientSubscribed = false,
  isHmisSubscribed = false,
  isIpdSubscribed = false,
  isLabSubscribed = false,
  isMedicalSubscribed = false,
  isOpdSubscribed = false,
  isBookingSubscribed = false,
  isPayrollSubscribed = false
}: Partial<ReturnType<typeof useModuleSubscriptions>>) {
  return {
    bill: {
      title: "Billing",
      description: "Manage bill settings",
      subscribed: isBillSubscribed
    },
    calendar: {
      title: "Calendar",
      description: "Manage calendar settings",
      subscribed: isBookingSubscribed
    },
    client: {
      title: "Client",
      description: "Manage client settings",
      subscribed: isClientSubscribed
    },
    hmis: {
      title: "HMIS",
      description: "Manage HMIS settings",
      subscribed: isHmisSubscribed
    },
    ipd: {
      title: "IPD",
      description: "Manage IPD settings",
      subscribed: isIpdSubscribed
    },
    lab: {
      title: "Lab",
      description: "Manage lab settings",
      subscribed: isLabSubscribed
    },
    medical: {
      title: "Medical",
      description: "Manage medical settings",
      subscribed: isMedicalSubscribed
    },
    opd: {
      title: "OPD",
      description: "Manage print and form settings for OPD",
      subscribed: isOpdSubscribed
    },
    stock: {
      title: "Stock",
      description: "Manage stock settings",
      subscribed: isStockSubscribed
    },
    attendance: {
      title: "Attendance",
      description: "Manage leaves, holidays, shift settings",
      subscribed: isAttendanceSubscribed
    },
    payroll: {
      title: "Payroll",
      description: "Mange ctc and other payroll settings",
      subscribed: isPayrollSubscribed
    }
  } as const;
}

type SettingKey = keyof ReturnType<typeof getSettings>;

const ModuleSettings = (): React.ReactNode => {
  const subscriptions = useModuleSubscriptions();

  const settings = getSettings(subscriptions);

  const [openSetting, setOpenSetting] = useState<Record<SettingKey, boolean>>({
    bill: false,
    calendar: false,
    client: false,
    hmis: false,
    ipd: false,
    lab: false,
    medical: false,
    opd: false,
    stock: false,
    attendance: false,
    payroll: false
  });

  const settingEntries = (
    Object.entries(settings) as [SettingKey, (typeof settings)[SettingKey]][]
  ).filter(([, item]) => item?.subscribed === true);

  if (settingEntries.length === 0) {
    return (
      <Box
        sx={{
          height: "90vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column"
        }}
      >
        <Typography variant="h6" color="text.secondary">
          No modules subscribed/enabled.
        </Typography>
        <Typography>
          To access settings, please enable/subscribe to a module from subscription.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        pb: 4,
        pr: { xs: 0, sm: 2 },
        height: "calc(100vh - 70px)",
        overflowY: "auto",
        overflowX: "hidden"
      }}
    >
      <Grid container spacing={2} p={{ xs: 1, sm: 2 }}>
        {settingEntries.map(([key, item]) => (
          <Grid key={key} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardActionArea
                data-testmation={key}
                onClick={() =>
                  setOpenSetting((prev) => {
                    const reset = Object.keys(prev).reduce(
                      (acc, k) => ({
                        ...acc,
                        [k]: false
                      }),
                      {} as Record<SettingKey, boolean>
                    );

                    return {
                      ...reset,
                      [key]: true
                    };
                  })
                }
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Box display="flex" gap={2} alignItems="center" justifyContent="space-between">
                    <div>
                      <Typography gutterBottom variant="h6">
                        {item.title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {item.description}
                      </Typography>
                    </div>

                    <KeyboardArrowRightIcon />
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <BillSettingsPanel
        renderSettingButton={false}
        open={openSetting.bill}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, bill: open }))}
      />

      <CalendarSettingsPanel
        renderSettingButton={false}
        open={openSetting.calendar}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, calendar: open }))}
        onViewChange={() => null}
      />

      <ClientSettingsPanel
        renderSettingButton={false}
        open={openSetting.client}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, client: open }))}
      />

      <HmisSettingsPanel
        renderSettingButton={false}
        open={openSetting.hmis}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, hmis: open }))}
      />

      <IpdSettingsPanel
        renderSettingButton={false}
        open={openSetting.ipd}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, ipd: open }))}
      />

      <LabSettingsPanel
        renderSettingButton={false}
        open={openSetting.lab}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, lab: open }))}
      />

      <MedicalSettingsPanel
        renderSettingButton={false}
        open={openSetting.medical}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, medical: open }))}
      />

      <OpdSettingsPanel
        renderSettingButton={false}
        open={openSetting.opd}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, opd: open }))}
      />

      <StockSettingsPanel
        renderSettingButton={false}
        open={openSetting.stock}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, stock: open }))}
      />

      <AttendanceSettingsPanel
        renderSettingButton={false}
        open={openSetting.attendance}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, attendance: open }))}
      />

      <PayrollSettingsPanel
        renderSettingButton={false}
        open={openSetting.payroll}
        onOpenChange={(open) => setOpenSetting((prev) => ({ ...prev, payroll: open }))}
      />
    </Box>
  );
};

export default ModuleSettings;
